import React, { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { toast } from "sonner";
import { formatCurrency } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Building2, Plus, Pencil, Power, Archive } from "lucide-react";

const TIPI = ["distributore carburanti", "bar", "shop", "autolavaggio", "attività combinata"];
const empty = {
  name: "", code: "", brand: "", types: [], address: "", comune: "", provincia: "", piva: "",
  responsabile: "", email: "", telefono: "", status: "attivo", data_apertura: "", note: "",
  soglia_da_versare: 3000, timezone: "Europe/Rome", logo: "",
};

export default function PuntiVendita() {
  const { stores, reloadStores } = useStore();
  const [all, setAll] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const load = async () => {
    const { data } = await api.get("/stores", { params: { include_archived: true } });
    setAll(data);
  };
  React.useEffect(() => { load(); }, []);

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (s) => { setForm({ ...empty, ...s }); setEditId(s.id); setOpen(true); };

  const toggleType = (t) =>
    setForm((f) => ({ ...f, types: f.types.includes(t) ? f.types.filter((x) => x !== t) : [...f.types, t] }));

  const save = async () => {
    try {
      const payload = { ...form, soglia_da_versare: parseFloat(form.soglia_da_versare) || 0 };
      if (editId) await api.put(`/stores/${editId}`, payload);
      else await api.post("/stores", payload);
      toast.success(editId ? "Punto vendita aggiornato" : "Punto vendita creato");
      setOpen(false); load(); reloadStores();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const setStatus = async (s, status) => {
    try {
      await api.post(`/stores/${s.id}/status`, { status });
      toast.success(`${s.name}: ${status}`);
      load(); reloadStores();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const badge = (st) => ({
    attivo: "bg-emerald-500/15 text-emerald-400",
    sospeso: "bg-amber-500/15 text-amber-400",
    archiviato: "bg-slate-500/15 text-slate-400",
  }[st] || "");

  return (
    <div className="space-y-6" data-testid="punti-vendita-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-extrabold tracking-tight sm:text-3xl">Punti Vendita</h1>
          <p className="text-sm text-muted-foreground">Crea, modifica, sospendi o archivia le sedi. I dati restano sempre separati.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2 font-bold" onClick={openNew} data-testid="add-store-btn"><Plus className="h-4 w-4" /> Nuova sede</Button>
          </DialogTrigger>
          <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto" data-testid="store-dialog">
            <DialogHeader><DialogTitle>{editId ? "Modifica" : "Nuovo"} punto vendita</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Denominazione *" span><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-11" data-testid="store-name-input" /></Field>
              <Field label="Codice interno"><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} className="h-11" /></Field>
              <Field label="Marchio"><Input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} className="h-11" placeholder="Enilive / Q8..." data-testid="store-brand-input" /></Field>
              <Field label="Comune"><Input value={form.comune} onChange={(e) => setForm({ ...form, comune: e.target.value })} className="h-11" /></Field>
              <Field label="Provincia"><Input value={form.provincia} onChange={(e) => setForm({ ...form, provincia: e.target.value })} className="h-11" /></Field>
              <Field label="Indirizzo" span><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="h-11" /></Field>
              <Field label="P.IVA / Società"><Input value={form.piva} onChange={(e) => setForm({ ...form, piva: e.target.value })} className="h-11" /></Field>
              <Field label="Responsabile"><Input value={form.responsabile} onChange={(e) => setForm({ ...form, responsabile: e.target.value })} className="h-11" /></Field>
              <Field label="Email"><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-11" /></Field>
              <Field label="Telefono"><Input value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} className="h-11" /></Field>
              <Field label="Data apertura"><Input type="date" value={form.data_apertura} onChange={(e) => setForm({ ...form, data_apertura: e.target.value })} className="h-11" /></Field>
              <Field label="Soglia da versare (€)"><Input type="number" value={form.soglia_da_versare} onChange={(e) => setForm({ ...form, soglia_da_versare: e.target.value })} className="h-11 tabular-nums" data-testid="store-soglia-input" /></Field>
              <Field label="Stato">
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger className="h-11"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="attivo">Attivo</SelectItem>
                    <SelectItem value="sospeso">Sospeso</SelectItem>
                    <SelectItem value="archiviato">Archiviato</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Tipologie di attività" span>
                <div className="flex flex-wrap gap-2">
                  {TIPI.map((t) => (
                    <button type="button" key={t} onClick={() => toggleType(t)}
                      className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${form.types.includes(t) ? "border-primary bg-primary/20 text-primary" : "border-border text-muted-foreground"}`}>
                      {t}
                    </button>
                  ))}
                </div>
              </Field>
              <Field label="Note" span><Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} /></Field>
            </div>
            <DialogFooter><Button onClick={save} className="font-bold" data-testid="store-save-btn">Salva</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {(all || []).map((s) => (
          <div key={s.id} className="rounded-xl border border-border bg-card p-5" data-testid={`store-card-${s.id}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2">
                <Building2 className="h-5 w-5 text-primary" />
                <div>
                  <div className="font-display font-bold">{s.name}</div>
                  <div className="text-xs text-muted-foreground">{s.brand} · {s.comune} {s.provincia && `(${s.provincia})`}</div>
                </div>
              </div>
              <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase ${badge(s.status)}`}>{s.status}</span>
            </div>
            <div className="mt-3 text-xs text-muted-foreground">
              Codice: {s.code || "—"} · Soglia: {formatCurrency(s.soglia_da_versare)}
              {s.types?.length ? <div className="mt-1">{s.types.join(", ")}</div> : null}
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <Button variant="secondary" size="sm" className="gap-1" onClick={() => openEdit(s)} data-testid={`edit-store-${s.id}`}><Pencil className="h-3.5 w-3.5" /> Modifica</Button>
              {s.status !== "attivo" && <Button variant="ghost" size="sm" className="gap-1 text-emerald-400" onClick={() => setStatus(s, "attivo")}><Power className="h-3.5 w-3.5" /> Attiva</Button>}
              {s.status === "attivo" && <Button variant="ghost" size="sm" className="gap-1 text-amber-400" onClick={() => setStatus(s, "sospeso")}><Power className="h-3.5 w-3.5" /> Sospendi</Button>}
              {s.status !== "archiviato" && <Button variant="ghost" size="sm" className="gap-1 text-slate-400" onClick={() => setStatus(s, "archiviato")} data-testid={`archive-store-${s.id}`}><Archive className="h-3.5 w-3.5" /> Archivia</Button>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Field({ label, children, span }) {
  return (
    <div className={span ? "col-span-2" : ""}>
      <Label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}
